<template>
  <section v-if="!isReady" class="reports">
    <p-loading />
  </section>

  <section v-else class="reports">
    <header>
      <h1>Reports</h1>
      <section class="action-wrapper">
        <router-link to="/reports/create">+ new</router-link>
      </section>
    </header>
    <p-list
      name="reports"
      :data="collection"
      :total="total"
      :metadata="{
        name: { label: 'Name', width: 'minmax(0, 3fr)' },
        description: { label: 'Description', width: 'minmax(0, 3fr)' },
        tags: { label: 'Tags', width: 'minmax(100px, 2fr)' },
        organizations: { label: 'Organizations', width: 'minmax(100px, 2fr)', labelCallback: getOrganizationName },
        hideInReports: { label: 'Hidden', width: '90px' },
        modifiedAt: { label: 'Last modification', width: '175px' }
      }"
      @edit="edit"
    >
      <template #name="{ item }"> {{ item.name }}.{{ item.type }}</template>
      <template #__actions="{ item }">
        <p-button v-if="item.formId" color="secondary" variant="text" title="Delete this report" @click="deleteReport(item)">Delete</p-button>
        <p-button variant="text" title="Edit this report" @click="edit(item.id)"> &#9998;</p-button>
      </template>
    </p-list>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import Button from './../common/Button';
import Loading from './../common/Loading';
import List from './../common/List';

export default {
  components: {
    'p-button': Button,
    'p-loading': Loading,
    'p-list': List
  },
  data() {
    return {
      isReady: false
    };
  },
  computed: {
    ...mapState({
      collection: s => s.reports.collection,
      total: s => s.reports.total,
      organizationsCollection: s => s.organizations.collection
    })
  },
  async created() {
    await Promise.all([this.$store.dispatch('reports/getCollection'), this.$store.dispatch('organizations/getCollection')]);
    this.isReady = true;
  },
  methods: {
    async edit(id) {
      this.$router.push({ path: `/reports/${id}/edit` });
    },
    async deleteReport(item) {
      if (!item?.formId) {
        return;
      }

      const confirmResult = await this.$confirm({
        title: 'Delete report?',
        message: `Are you sure you want to delete report '${item.name}'?\n\nThis action can't be undone.`,
        confirm: 'Delete'
      });

      if (!confirmResult) {
        return;
      }

      const lock = this.$lock?.();
      try {
        await this.$store.dispatch('reports/delete', item.id);
        this.$toast.success({
          title: 'Report deleted',
          message: `Report '${item.name}' (${item.id}) was successfully deleted.`
        });
      } catch (e) {
        const { message } = JSON.parse(await e.response.text());
        this.$toast.error({
          title: 'Failed to delete report',
          message: message || `Please, try again later or contact our development team.`
        });
      } finally {
        lock?.release?.();
      }
    },
    getOrganizationName(id) {
      return this.organizationsCollection.find(o => o.id === id)?.name ?? 'Unknown Organization';
    }
  }
};
</script>

<style scoped lang="scss">
.reports {
  display: grid;
  padding: 0 0.5rem 0 1rem;
  grid-template-columns: 1fr;
  grid-template-rows: max-content minmax(0, 1fr);
  grid-gap: 0.5rem;
  width: 100%;
  height: 100%;

  > header {
    padding: 0.5rem 0.5rem 0;
    display: flex;
    justify-content: space-between;

    box-sizing: border-box;
    h1 {
      color: var(--theme-on-surface);
      font-size: 1.5rem;
      font-weight: 700;
      margin-right: 0.25rem;
    }

    .action-wrapper {
      display: flex;
      justify-content: flex-end;
      align-items: center;
    }
  }
}
</style>
